import React, { useEffect, useState } from 'react';
import { JBAlert, JBButton } from '@jbhi-fi/lanyard-ui';
import IllustrationComponent from '../Illustration';
import CustomerHelper from '../../../helpers/CustomerHelper';
import { useMobileVerifyContext } from '../../../context/MobileVerifyContext';
import { MobileVerifyResponseType } from '../../../hooks/useMobileVerify';
import { useSetMobile } from '../../../hooks/useSetMobile/useSetMobile';
import { useVerifyLayout } from '../../../hooks/useVerifyLayout/useVerifyLayout';
import Landing from '../Landing';
import { useTrackingContext, trackingContext, formAction } from '@jbhi-fi/components/src/Tracking';
import TextField, { InputType } from '@jbhi-fi/lanyard-ui/components/TextField';
import { styles } from './LinkReceipts.css';

const customerHelper = new CustomerHelper();

const MOBILE_FIELD_ERROR_DEFAULT = 'Mobile phone number invalid, give it another go.';

const LinkReceiptsView = ({
  handleRedirect,
}: {
  handleRedirect: (data: MobileVerifyResponseType) => void;
}): JSX.Element => {
  const { setMobileVerifyData } = useMobileVerifyContext();
  const { tracker } = useTrackingContext();
  const { themeContext } = useVerifyLayout();
  const { hideSkipVerifyButton, receiptPhone, receiptCode } = themeContext ?? {};
  const suppressTracking = Boolean(receiptCode);
  const {
    saveError,
    submitting,
    handleSave,
    handleChange: handleNumberChange,
    isMobileValid,
    mobileInput,
  } = useSetMobile({
    handleRedirect,
    tracker,
    receiptPhone,
    receiptCode,
    suppressTracking,
  });
  const [skipError, setSkipError] = useState<boolean>(false);
  const [skipping, setSkipping] = useState<boolean>(false);

  useEffect(() => {
    document.title = 'Link in-store purchases - Account - JB Hi-Fi';
    tracker.group = 'verify mobile number';
    if (suppressTracking) {
      return;
    }
    tracker.trackFormEvent({ action: formAction.stepView });
    tracker.trackFormEvent({ action: formAction.start });
  }, []);

  const skipSavePhoneNumber = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.preventDefault();
    setSkipping(true);
    if (!suppressTracking) {
      tracker.trackClick({ label: 'Skip for now' });
    }

    try {
      const phoneVerifySkipped = await customerHelper.setCustomerPreferences(window.Jbhifi.shopifyCustomerId, {
        shopify_login_skipMobileVerification: 'true',
      });
      setMobileVerifyData(phoneVerifySkipped);
      if (phoneVerifySkipped.isSuccess) {
        window.location.href = '/account#/';
      } else {
        setSkipError(true);
      }
    } catch (error) {
      if (!suppressTracking) {
        tracker.trackError({
          errorMsg: 'There was a problem saving your preference',
          contextOverride: trackingContext.form,
        });
      }
      setSkipError(true);
      window.location.href = '/account#/';
    }
  };

  const handleSaveAndRedirect = async (
    event:
      | React.KeyboardEvent<HTMLInputElement>
      | React.FormEvent<HTMLFormElement>
      | React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    tracker.trackFormEvent({ label: 'Phone', action: formAction.fieldComplete });
    const { ok, response } = await handleSave();
    if (ok && response) {
      handleRedirect(response);
    }
  };

  return receiptPhone && !saveError ? (
    <Landing handleRedirect={handleRedirect} />
  ) : (
    <>
      <div className="alerts">
        {skipError ? (
          <JBAlert showIcon type="warning" heading="There was a problem saving your preference. Please try again." />
        ) : null}
      </div>
      <div className="page-width verify-section">
        <IllustrationComponent name="link" />
        <h2 className={styles.heading}>Link your in-store receipts</h2>
        <p className={styles.contentText}>
          Add your mobile number to your account and we&apos;ll automatically save any in-store receipts you receive to
          your account for safekeeping.
        </p>
        <form id="link_receipts" onSubmit={submitting ? event => event.preventDefault() : handleSaveAndRedirect}>
          <TextField
            id="mobile-input"
            name="mobile-input"
            label="Mobile phone"
            onChange={handleNumberChange}
            value={mobileInput}
            hasError={Boolean(saveError) || (!isMobileValid && Boolean(mobileInput))}
            errorMsg={(saveError && saveError?.heading) || MOBILE_FIELD_ERROR_DEFAULT}
            type={InputType.tel}
            onKeyDown={e => {
              e.key === 'Enter' && !submitting ? handleSaveAndRedirect(e) : null;
            }}
            maxLength={10}
          />
          <p className={styles.mobileInfoText}>We&apos;ll send you a 4-digit verification code</p>
          <JBButton
            type="primary"
            label={submitting ? 'Verifying phone number...' : 'Verify phone number'}
            fullWidth={true}
            onClick={e => (submitting ? null : handleSaveAndRedirect(e))}
            submit
          />
          {!hideSkipVerifyButton ? (
            <div className={styles.skipButton}>
              <JBButton
                type="link"
                label={skipping ? 'Saving preference...' : 'Skip for now'}
                fullWidth={true}
                onClick={e => (skipping ? null : skipSavePhoneNumber(e))}
              />
            </div>
          ) : null}
        </form>
      </div>
    </>
  );
};

export default LinkReceiptsView;
